import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, MapPin, Film, Tv, Star } from 'lucide-react';
import { getImageUrl, getPersonDetails } from '@/lib/api';

export const PersonPage = () => {
  const { id } = useParams();
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPerson = async () => {
      setLoading(true);
      try {
        const data = await getPersonDetails(id);
        setPerson(data);
      } catch (error) {
        console.error('Error fetching person:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPerson();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!person) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Persoon niet gevonden</p>
      </div>
    );
  }

  const profileImage = getImageUrl(person.profile_path, 'h632');
  const credits = person.combined_credits || {};
  const castCredits = credits.cast || [];
  const crewCredits = credits.crew || [];

  // Sort by popularity and get unique
  const sortedMovies = castCredits
    .filter(c => c.media_type === 'movie' && c.poster_path)
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 20);

  const sortedTv = castCredits
    .filter(c => c.media_type === 'tv' && c.poster_path)
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 20);

  // Get directing credits
  const directingCredits = crewCredits
    .filter(c => c.job === 'Director' && c.poster_path)
    .sort((a, b) => (b.popularity || 0) - (a.popularity || 0))
    .slice(0, 10);

  const age = person.birthday 
    ? Math.floor((new Date() - new Date(person.birthday)) / (365.25 * 24 * 60 * 60 * 1000))
    : null;

  return (
    <div data-testid="person-page" className="min-h-screen">
      {/* Back Button */}
      <div className="p-4 md:p-8">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-white/70 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Terug</span>
        </Link>
      </div>

      {/* Profile Section */}
      <section className="px-4 md:px-8 lg:px-12 pb-12">
        <div className="flex flex-col md:flex-row gap-8 max-w-6xl">
          {/* Profile Image */}
          <div className="w-48 md:w-64 flex-shrink-0 mx-auto md:mx-0">
            {profileImage ? (
              <img 
                src={profileImage}
                alt={person.name}
                className="w-full rounded-xl shadow-2xl"
              />
            ) : (
              <div className="w-full aspect-[2/3] rounded-xl bg-gray-800 flex items-center justify-center">
                <span className="text-4xl font-bold text-gray-600">{person.name?.charAt(0)}</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1">
            <h1 className="text-3xl md:text-4xl font-display font-bold mb-4">{person.name}</h1>
            
            {/* Meta Info */}
            <div className="flex flex-wrap gap-4 text-sm text-gray-400 mb-6">
              {person.birthday && (
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {new Date(person.birthday).toLocaleDateString('nl-NL', { 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                  {age && ` (${age} jaar)`}
                </span>
              )}
              {person.place_of_birth && (
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {person.place_of_birth}
                </span>
              )}
            </div>

            {/* Known For */}
            {person.known_for_department && (
              <p className="text-indigo-400 text-sm mb-4">
                Bekend van: {person.known_for_department === 'Acting' ? 'Acteerwerk' : person.known_for_department}
              </p>
            )}

            {/* Biography */}
            {person.biography && (
              <div className="mb-6">
                <h2 className="text-lg font-display font-semibold mb-2">Biografie</h2>
                <p className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {person.biography}
                </p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Filmography */}
      <section className="px-4 md:px-8 lg:px-12 pb-12">
        {/* Movies */}
        {sortedMovies.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Film className="w-5 h-5 text-indigo-400" />
              <h2 className="text-xl font-display font-bold">Films</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
              {sortedMovies.map(movie => (
                <Link 
                  key={`movie-${movie.id}-${movie.credit_id}`}
                  to={`/movie/${movie.id}`}
                  className="flex-shrink-0 w-32 group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2 group-hover:ring-2 group-hover:ring-indigo-500 transition-all">
                    <img 
                      src={getImageUrl(movie.poster_path, 'w185')}
                      alt={movie.title}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm font-medium line-clamp-2">{movie.title}</p>
                  {movie.character && (
                    <p className="text-xs text-gray-500 line-clamp-1">als {movie.character}</p>
                  )}
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <span>{movie.vote_average?.toFixed(1)}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* TV Shows */}
        {sortedTv.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <Tv className="w-5 h-5 text-purple-400" />
              <h2 className="text-xl font-display font-bold">Series</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
              {sortedTv.map(show => (
                <Link 
                  key={`tv-${show.id}-${show.credit_id}`}
                  to={`/tv/${show.id}`}
                  className="flex-shrink-0 w-32 group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2 group-hover:ring-2 group-hover:ring-indigo-500 transition-all">
                    <img 
                      src={getImageUrl(show.poster_path, 'w185')}
                      alt={show.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm font-medium line-clamp-2">{show.name}</p>
                  {show.character && (
                    <p className="text-xs text-gray-500 line-clamp-1">als {show.character}</p>
                  )}
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Directing */}
        {directingCredits.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Film className="w-5 h-5 text-yellow-400" />
              <h2 className="text-xl font-display font-bold">Regisseur</h2>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-4 hide-scrollbar">
              {directingCredits.map(item => (
                <Link 
                  key={`dir-${item.id}-${item.credit_id}`}
                  to={`/${item.media_type}/${item.id}`}
                  className="flex-shrink-0 w-32 group"
                >
                  <div className="aspect-[2/3] rounded-lg overflow-hidden bg-gray-800 mb-2 group-hover:ring-2 group-hover:ring-indigo-500 transition-all">
                    <img 
                      src={getImageUrl(item.poster_path, 'w185')}
                      alt={item.title || item.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <p className="text-sm font-medium line-clamp-2">{item.title || item.name}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default PersonPage;
